import { Permission } from "encamp-prisma/generated/browser";

/**
 * Plain-English meaning of each customer permission. The `"""` descriptions on
 * `enum Permission` in schema.graphql must match these sentences.
 */
export const PERMISSION_DESCRIPTIONS = {
  [Permission.RouteEPCRA]:
    "Access EPCRA chemical reporting, including agencies such as fire departments and LEPCs.",
  [Permission.RouteRCRA]: "Access the RCRA waste area.",
  [Permission.RouteFacilities]: "Open the Facilities page.",
  [Permission.RouteCalendar]: "Open the compliance calendar.",
  [Permission.RouteContacts]: "Open Contacts, including program contact lists.",
  [Permission.RouteDocuments]: "Open Documents, including program document lists.",
  [Permission.RouteEPCRAOverview]: "Open the Chemicals overview.",
  [Permission.RouteEPCRATierII]:
    "Access Tier II reporting, including agencies such as fire departments and LEPCs.",
  [Permission.RouteEPCRAReports]: "Open the chemical reports list.",
  [Permission.RouteEPCRAProducts]:
    "Open the chemical and product catalogs. Catalog edits use the separate catalog permissions.",
  [Permission.RouteEPCRACredentials]:
    "Open the Credentials page. Revealing passwords and editing credentials use separate permissions.",
  [Permission.RouteInsights]: "Open Insights.",
  [Permission.RouteScout]: "Open Scout Regulatory Advisor when role-scoped access is enabled.",
  [Permission.AccessScoutPro]:
    "Allows a user to access the paid Scout Pro package when Scout Pro role-scoped access is enabled.",
  [Permission.ReadOrgEventSequences]:
    "See the organization's recurring calendar and task sequences.",
  [Permission.WriteOrgEventSequences]:
    "Create and edit the organization's recurring calendar and task sequences.",
  [Permission.ReadAllFacility]:
    "See every facility in the organization, including ones the user is not assigned to.",
  [Permission.ReadAllFacilityMinimal]:
    "Read a reduced facility record. This does not grant access to every facility.",
  [Permission.WriteAllFacility]:
    "Edit every facility in the organization. Holding this also grants access to all of them.",
  [Permission.WriteFacilityRcra]:
    "Edit a facility's RCRA profile, including the inputs used for waste classification.",
  [Permission.WriteFacilityBillingContact]:
    'Change a facility\'s billing contact: the "Use Encamp as billing contact" setting and the EPCRA billing contact role.',
  [Permission.CreateFacility]:
    "Create a facility. A customer with this permission is still refused; only staff can create facilities.",
  [Permission.EditOwnFacilityPermissions]:
    "Assign yourself to facilities when facility self-assignment is turned on for the organization.",
  [Permission.ReadAllUserTenant]: "See the organization's full user list.",
  [Permission.WriteAllUserTenant]:
    "Edit users in the organization, including role, status, and facility assignments.",
  [Permission.CreateUserTenant]: "Invite a user to the organization.",
  [Permission.ReadUserTenantRole]: "See which role a user has in the organization.",
  [Permission.WriteUserTenantRole]: "Change the role assigned to a user.",
  [Permission.ReadRole]: "See the organization's roles.",
  [Permission.WriteRole]:
    "Create and edit roles. A customer with this permission is still refused; only staff can change roles.",
  [Permission.ReadPerson]: "See contacts.",
  [Permission.WritePerson]: "Add and edit contacts.",
  [Permission.ReadFacilityContact]: "See which contacts are assigned to a facility.",
  [Permission.WriteFacilityContact]:
    "Add, edit, and remove a facility's contacts. Changing the billing contact also requires WriteFacilityBillingContact.",
  [Permission.ReadSystemAuditLog]: "See the system audit log.",
  [Permission.ReadUserActivityLog]: "See the user activity log.",
  [Permission.CreateDataCollectionTask]: "Create data collection tasks.",
  [Permission.WriteDataCollectionTask]: "Edit every field on a data collection task.",
  [Permission.FulfillDataCollectionTask]:
    "Enter contacts and inventory, and start new Tier II reports, at facilities the user can access.",
  [Permission.WriteDataCollectionTaskTemplate]:
    "Edit data collection task templates. Deprecated, and hidden from the role editor.",
  [Permission.WriteAllCalendarEvent]: "Create and edit calendar events across the organization.",
  [Permission.VerifyTierIIReport]:
    "Verify and submit a Tier II report at facilities the user is assigned to. ReadAllFacility does not extend this to every facility.",
  [Permission.ManageReviewerRequirements]:
    "Decide whether a Tier II report at an assigned facility requires reviewers.",
  [Permission.ViewCredentials]:
    "Reveal passwords on credentials the organization owns. Encamp-owned credentials stay hidden, and this does not allow editing them.",
  [Permission.WriteCredential]: "Create, edit, and delete credentials.",
  [Permission.WriteDocument]: "Create, edit, and delete documents.",
  [Permission.MergeChemicals]:
    "Merge duplicate chemical catalog entries. By convention, grant alongside RouteEPCRAProducts (catalog access).",
  [Permission.WriteChemicalCatalog]:
    "Add and edit chemicals and products in the catalog. Facility inventory entry does not need it.",
  [Permission.DeleteChemicalCatalog]: "Delete chemicals and products from the catalog.",
} as const satisfies Record<Permission, string>;

export function describePermission(permission: string): string | undefined {
  for (const [key, description] of Object.entries(PERMISSION_DESCRIPTIONS)) {
    if (key === permission) {
      return description;
    }
  }
  return undefined;
}
